/*
 * Stripe success redirect target (Phase E). Reads session_id, polls the
 * backend status endpoint until the payment settles + tenant is provisioned.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX = 8;

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setState("error"); return; }
    if (attempt >= MAX) { setState("timeout"); return; }
    try {
      const r = await axios.get(`${API}/api/school-onboarding/checkout/status/${sessionId}`);
      if (r.data.payment_status === "paid") { setState("paid"); return; }
      if (r.data.status === "expired") { setState("timeout"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch { setTimeout(() => poll(attempt + 1), 2000); }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f9fafb] px-4" data-testid="checkout-success-page">
      <div className="max-w-lg w-full bg-white rounded-2xl border border-[#e5e7eb] p-10 text-center">
        {state === "checking" && (<><Loader2 className="h-10 w-10 text-[#007a9e] mx-auto animate-spin" /><h1 className="mt-4 text-2xl font-bold text-[#111827]">Confirming your payment…</h1><p className="mt-2 text-[14px] text-[#6b7280]">This only takes a moment.</p></>)}
        {state === "paid" && (<><CheckCircle2 className="h-12 w-12 text-[#059669] mx-auto" /><h1 className="mt-4 text-2xl font-bold text-[#111827]" data-testid="checkout-success-paid">Payment successful!</h1><p className="mt-3 text-[14px] text-[#4b5563]">Your MySchoolRx workspace is being set up. Check your email for login details, then sign in to begin.</p><Button onClick={() => navigate("/diagnostic/login")} className="mt-6 bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="checkout-success-login">Sign in to your workspace</Button></>)}
        {state === "timeout" && (<><AlertCircle className="h-10 w-10 text-[#d97706] mx-auto" /><h1 className="mt-4 text-2xl font-bold text-[#111827]">Still processing</h1><p className="mt-2 text-[14px] text-[#6b7280]">Your payment is taking longer than expected. You'll receive a confirmation email shortly.</p><Button onClick={() => navigate("/myschoolrx")} variant="outline" className="mt-6">Back to overview</Button></>)}
        {state === "error" && (<><AlertCircle className="h-10 w-10 text-[#b91c1c] mx-auto" /><h1 className="mt-4 text-2xl font-bold text-[#111827]">Something went wrong</h1><p className="mt-2 text-[14px] text-[#6b7280]">We couldn't verify your payment. Please contact support.</p></>)}
      </div>
    </div>
  );
}
